<template>
  <section class="revenue-statistics">
    <div class="page-heading">
      <div>
        <h1 class="title is-3">Umsatz nach Artikelgruppen</h1>
        <p class="subtitle is-6">
          Netto-Umsätze finalisierter Rechnungen nach Artikelgruppe
        </p>
      </div>
    </div>

    <form class="filter-panel box" @submit.prevent="fetchData">
      <b-field grouped group-multiline>
        <b-field label="Jahr">
          <b-select v-model="year" expanded>
            <option
                v-for="availableYear in years"
                :key="availableYear"
                :value="availableYear"
            >
              {{ availableYear }}
            </option>
          </b-select>
        </b-field>

        <b-field label="Zeitraum">
          <b-select v-model="month" expanded>
            <option :value="null">Gesamtes Jahr</option>
            <option
                v-for="availableMonth in months"
                :key="availableMonth.value"
                :value="availableMonth.value"
            >
              {{ availableMonth.label }}
            </option>
          </b-select>
        </b-field>

        <b-field class="submit-field">
          <b-button
              native-type="submit"
              type="is-primary"
              icon-pack="fa"
              icon-left="search"
              :loading="isLoading"
          >
            Auswerten
          </b-button>
        </b-field>
      </b-field>
    </form>

    <b-message
        v-if="errorMessage"
        type="is-danger"
        has-icon
        aria-close-label="Fehlermeldung schließen"
        @close="errorMessage = ''"
    >
      {{ errorMessage }}
    </b-message>

    <template v-if="hasLoaded">
      <div class="summary-columns columns is-multiline">
        <div class="column is-12-mobile is-4-tablet">
          <div class="summary-card box">
            <span class="summary-label">Gesamtumsatz</span>
            <strong class="summary-value">{{ formatEuro(totalRevenue) }}</strong>
            <span class="summary-detail">{{ selectedPeriod }}</span>
          </div>
        </div>
        <div class="column is-12-mobile is-4-tablet">
          <div class="summary-card box">
            <span class="summary-label">Artikelgruppen</span>
            <strong class="summary-value">{{ data.length }}</strong>
            <span class="summary-detail">inklusive nicht zugeordneter Positionen</span>
          </div>
        </div>
        <div class="column is-12-mobile is-4-tablet">
          <div class="summary-card box">
            <span class="summary-label">Umsatzstärkste Gruppe</span>
            <strong class="summary-value summary-value--category">
              {{ highestRevenueCategory ? highestRevenueCategory.article_category_name : '–' }}
            </strong>
            <span class="summary-detail">
              {{ highestRevenueCategory ? formatEuro(highestRevenueCategory.revenue) : 'Keine Daten' }}
            </span>
          </div>
        </div>
      </div>

      <div v-if="data.length" class="table-panel box">
        <div class="table-heading">
          <div>
            <h2 class="title is-5">Verteilung</h2>
            <p class="has-text-grey">{{ selectedPeriod }}</p>
          </div>
          <b-tag type="is-light">{{ data.length }} Einträge</b-tag>
        </div>

        <b-table
            :data="data"
            :mobile-cards="true"
            default-sort="article_category_name"
            default-sort-direction="asc"
            aria-current-label="Aktuelle Seite"
            aria-next-label="Nächste Seite"
            aria-page-label="Seite"
            aria-previous-label="Vorherige Seite"
        >
          <b-table-column
              field="article_category_name"
              label="Artikelgruppe"
              sortable
              v-slot="props"
          >
            <span>{{ props.row.article_category_name }}</span>
            <b-tag
                v-if="props.row.article_category_id === null"
                class="special-category-tag"
                type="is-warning is-light"
                size="is-small"
            >
              Sondergruppe
            </b-tag>
          </b-table-column>

          <b-table-column
              field="revenue"
              label="Netto-Umsatz"
              numeric
              sortable
              v-slot="props"
          >
            <strong>{{ formatEuro(props.row.revenue) }}</strong>
          </b-table-column>

          <b-table-column
              field="share"
              label="Anteil"
              numeric
              v-slot="props"
          >
            {{ formatPercentage(props.row.revenue) }}
          </b-table-column>
        </b-table>
      </div>

      <b-message v-else type="is-info" has-icon>
        Für {{ selectedPeriod }} wurden keine Umsätze gefunden.
      </b-message>

      <b-message class="calculation-note" type="is-info">
        Berücksichtigt werden ausschließlich finalisierte Rechnungen. Der Umsatz ist netto nach
        Rechnungsrabatt; „Ohne Artikel“ bezeichnet Positionen ohne
        verknüpften Artikel, „Ohne Artikelgruppe“ Artikel ohne Kategorie. Gemischte Positionen (Artikel mit verschmolzener Dienstleistung) werden in dieser Übersicht voll der Artikelgruppe des verknüpften Artikels zugeordnet.
      </b-message>
    </template>

    <b-loading :active="isLoading" :is-full-page="false" :can-cancel="false" />
  </section>
</template>

<script>
import apiservice from '@/services/apiservice';

export default {
  name: 'StatsRevenueArticleCategory',
  data() {
    return {
      data: [],
      year: new Date().getFullYear(),
      month: null,
      appliedYear: new Date().getFullYear(),
      appliedMonth: null,
      isLoading: false,
      hasLoaded: false,
      errorMessage: '',
      requestId: 0
    };
  },
  computed: {
    years() {
      const startYear = 2023;
      const currentYear = new Date().getFullYear();

      return Array.from(
          { length: currentYear - startYear + 1 },
          (_, index) => startYear + index
      );
    },
    months() {
      return Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        label: new Intl.DateTimeFormat('de-DE', { month: 'long' })
            .format(new Date(2000, index, 1))
      }));
    },
    selectedPeriod() {
      if (this.appliedMonth === null) {
        return `Gesamtjahr ${this.appliedYear}`;
      }

      const selectedMonth = this.months.find(month => month.value === this.appliedMonth);
      return `${selectedMonth ? selectedMonth.label : ''} ${this.appliedYear}`.trim();
    },
    totalRevenue() {
      return this.data.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
    },
    highestRevenueCategory() {
      return this.data
          .filter(item => (item.article_category_name || '').trim().toLowerCase() !== 'ohne artikel')
          .reduce((highest, item) => {
            if (!highest || Number(item.revenue) > Number(highest.revenue)) {
              return item;
            }
            return highest;
          }, null);
    }
  },
  methods: {
    fetchData() {
      const currentRequestId = ++this.requestId;
      const requestedYear = this.year;
      const requestedMonth = this.month;
      const params = { year: requestedYear };
      if (requestedMonth !== null) {
        params.month = requestedMonth;
      }

      this.isLoading = true;
      this.errorMessage = '';

      apiservice()
          .get('stats/revenue/article-categories', { params })
          .then(response => {
            if (currentRequestId !== this.requestId) {
              return;
            }
            this.data = Array.isArray(response.data.data) ? response.data.data : [];
            this.appliedYear = requestedYear;
            this.appliedMonth = requestedMonth;
            this.hasLoaded = true;
          })
          .catch(error => {
            if (currentRequestId !== this.requestId) {
              return;
            }
            this.errorMessage = this.getErrorMessage(error);
          })
          .finally(() => {
            if (currentRequestId === this.requestId) {
              this.isLoading = false;
            }
          });
    },
    getErrorMessage(error) {
      const validationErrors = error && error.data;
      if (validationErrors && typeof validationErrors === 'object') {
        return Object.keys(validationErrors)
            .map(field => `${this.getFieldLabel(field)}: ${this.translateValidationMessage(validationErrors[field])}`)
            .join(' ');
      }

      if (error && error.message) {
        return error.message;
      }

      if (typeof error === 'string') {
        return error;
      }

      return 'Die Umsatzdaten konnten nicht abgerufen werden. Bitte versuche es erneut.';
    },
    getFieldLabel(field) {
      const labels = {
        year: 'Jahr',
        month: 'Monat'
      };
      return labels[field] || field;
    },
    translateValidationMessage(message) {
      const translations = {
        'must be between 1 and 12': 'muss zwischen 1 und 12 liegen',
        'must be between 1 and 9999': 'muss zwischen 1 und 9999 liegen'
      };
      return translations[message] || message;
    },
    formatEuro(amount) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
      }).format(Number(amount || 0));
    },
    formatPercentage(revenue) {
      if (this.totalRevenue === 0) {
        return '0,0 %';
      }

      return new Intl.NumberFormat('de-DE', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(Number(revenue || 0) / this.totalRevenue);
    }
  },
  beforeMount() {
    this.fetchData();
  }
};
</script>

<style scoped>
.revenue-statistics {
  position: relative;
  max-width: 1100px;
  min-height: 320px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
}

.page-heading {
  margin-bottom: 1.5rem;
}

.page-heading .subtitle {
  margin-top: -0.75rem;
  color: #6b7280;
}

.filter-panel {
  margin-bottom: 1.5rem;
}

.filter-panel >>> .field.is-grouped {
  align-items: flex-end;
}

.filter-panel >>> .numberinput,
.filter-panel >>> .select,
.filter-panel >>> .select select {
  min-width: 180px;
}

.submit-field {
  padding-bottom: 0.75rem;
}

.summary-columns {
  margin-bottom: 0.25rem;
}

.summary-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 145px;
  border-top: 3px solid #7957d5;
}

.summary-label {
  margin-bottom: 0.45rem;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.summary-value {
  margin-bottom: 0.35rem;
  color: #27272a;
  font-size: 1.75rem;
  line-height: 1.2;
}

.summary-value--category {
  font-size: 1.25rem;
}

.summary-detail {
  margin-top: auto;
  color: #6b7280;
  font-size: 0.875rem;
}

.table-panel {
  overflow: hidden;
  padding: 0;
}

.table-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 0.5rem;
}

.table-heading .title {
  margin-bottom: 0.15rem;
}

.table-panel >>> .table-wrapper {
  overflow-x: auto;
}

.table-panel >>> .table {
  width: 100%;
}

.table-panel >>> .table th,
.table-panel >>> .table td {
  padding: 0.75rem 1.5rem;
  vertical-align: middle;
}

.special-category-tag {
  margin-left: 0.6rem;
}

.calculation-note {
  margin-top: 1.5rem;
}

@media screen and (max-width: 768px) {
  .revenue-statistics {
    padding-top: 1.25rem;
  }

  .filter-panel >>> .field.is-grouped {
    align-items: stretch;
  }

  .filter-panel >>> .control,
  .filter-panel >>> .numberinput,
  .filter-panel >>> .select,
  .filter-panel >>> .select select,
  .submit-field .button {
    width: 100%;
  }

  .submit-field {
    padding-bottom: 0;
  }

  .table-heading {
    align-items: flex-start;
    padding: 1rem;
  }
}
</style>
